<template>
  <div class="nav-header">
    <!-- 固钉组件，通过设置 offset 属性来改变吸顶距离，默认值为 0。 -->
    <el-affix :offset="0">
      <!-- 设置背景色为白色、高度为 64px，padding-right 为 4， border-bottom 为 slate 100 -->
      <div class="header-blox">
        <!-- 左边栏收缩、展开 -->
        <div class="header-left" @click="handleMenuWidth">
          <el-icon>
            <Fold v-if="isCollapse" />
            <Expand v-else />
          </el-icon>
        </div>

        <!-- 右边容器 -->
        <div class="header-right">
          <!-- 点击刷新页面 -->
          <el-tooltip class="box-item" effect="dark" content="刷新" placement="bottom">
            <div class="box-item-tip" @click="handleRefresh">
              <el-icon>
                <Refresh />
              </el-icon>
            </div>
          </el-tooltip>

          <!-- 点击全屏展示 -->
          <el-tooltip class="box-item" effect="dark" content="全屏" placement="bottom">
            <div class="box-item-tip" @click="toggle">
              <el-icon>
                <FullScreen v-if="!isFullscreen" />
                <Aim v-else />
              </el-icon>
            </div>
          </el-tooltip>

          <!-- 登录用户头像 -->
          <el-dropdown class="flex items-center justify-center" @command="handleCommand">
            <span class="header-avatar el-dropdown-link">
              <!-- 头像 Avatar -->
              <el-avatar :size="25" class="custom-avatar" src="https://img.quanxiaoha.com/quanxiaoha/f97361c0429d4bb1bc276ab835843065.jpg" />
              {{ $store.state.login.userinfo.userName }}
              <el-icon class="el-icon--right">
                <arrow-down />
              </el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="updatePassword">修改密码</el-dropdown-item>
                <el-dropdown-item command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <!-- 修改密码 -->
      <FormDialog ref="formDialogRef" title="修改密码" destroyOnClose @submit="onSubmit">
        <el-form ref="formRef" :rules="rules" :model="updatePasswordForm">
          <el-form-item label="用户名" prop="username" label-width="120px" size="large">
            <!-- 输入框组件 -->
            <el-input v-model="updatePasswordForm.username" placeholder="请输入用户名" clearable disabled />
          </el-form-item>
          <el-form-item label="新密码" prop="password" label-width="120px" size="large">
            <el-input type="password" v-model="updatePasswordForm.password" placeholder="请输入新密码" clearable show-password />
          </el-form-item>
          <el-form-item label="确认新密码" prop="rePassword" label-width="120px" size="large">
            <el-input type="password" v-model="updatePasswordForm.rePassword" placeholder="请确认新密码" clearable show-password />
          </el-form-item>
        </el-form>
      </FormDialog>
    </el-affix>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useFullscreen } from '@vueuse/core'
// import { updateAdminPassword } from '@/api/admin/user'
import { useRouter } from 'vue-router'
import { showMessage, showModel } from '@/utils/common-util'
import FormDialog from 'components/base/FormDialog.vue'
import { Aim, ArrowDown, Expand, Fold, FullScreen, Refresh } from '@element-plus/icons-vue'
import { useStore } from '@/store'
import { ElForm } from 'element-plus'

const router = useRouter()

// isFullscreen 表示当前是否处于全屏；toggle 用于动态切换全屏、非全屏
const { isFullscreen, toggle } = useFullscreen()

// 引入了菜单 Store
// const menuStore = useMenuStore()
// 引入了用户 Store
const store = useStore()

const emit = defineEmits<{ foldChange: [isCollapse: boolean] }>()

const isCollapse = ref(false)
// icon 点击事件
const handleMenuWidth = () => {
  isCollapse.value = !isCollapse.value
  emit('foldChange', isCollapse.value)
}

// 刷新页面
const handleRefresh = () => location.reload()

// 对话框是否显示
const formDialogRef = ref<InstanceType<typeof FormDialog>>()

// 下拉菜单事件处理
const handleCommand = (command: any) => {
  // 更新密码
  if (command == 'updatePassword') {
    // 显示修改密码对话框
    formDialogRef.value.open()
  } else if (command == 'logout') {
    // 退出登录
    logout()
  }
}

// 退出登录
async function logout() {
  try {
    await showModel('是否确认要退出登录？')
    await store.dispatch('login/logoutAction')
    showMessage('退出成功！')
    await router.replace('/login')
  } catch {
    // 取消确认或退出失败：不跳转
  }
}

// 表单引用
const formRef = ref<InstanceType<typeof ElForm>>()
const username = ref(store.state.login.userinfo.userName || '')
// 修改用户密码表单对象
const updatePasswordForm = reactive({
  username: username.value,
  password: '',
  rePassword: ''
})

// 规则校验
const rules = {
  username: [
    {
      required: true,
      message: '用户名不能为空',
      trigger: 'blur'
    }
  ],
  password: [
    {
      required: true,
      message: '密码不能为空',
      trigger: 'blur'
    }
  ],
  rePassword: [
    {
      required: true,
      message: '确认密码不能为空',
      trigger: 'blur'
    }
  ]
}

const onSubmit = () => {
  formRef.value?.validate((valid) => {
    if (!valid) {
      return
    }
    if (updatePasswordForm.password != updatePasswordForm.rePassword) {
      showMessage('两次密码输入不一致，请检查！', 'error')
      return
    }
    formDialogRef.value.showBtnLoading()
    store
      .dispatch('system/updatePasswordAction', { ...updatePasswordForm })
      .then(() => {
        showMessage('修改密码成功，请重新登录！')
        formDialogRef.value.closeBtnLoading()
        formDialogRef.value.close()
        router.replace('/login')
      })
      .catch(() => {
        showMessage('修改密码失败！', 'error')
      })
      .finally(() => {
        formDialogRef.value.closeBtnLoading()
      })
  })
}

// const emit = defineEmits(['submit'])
// const submit = () => emit('submit')
</script>

<style scoped lang="less">
.nav-header {
  // 整体导航头部样式
  .header-blox {
    background-color: white;
    height: 64px;
    display: flex;
    padding-right: 1rem;
    border-bottom: 1px solid #f1f5f9;

    // 左侧菜单按钮样式
    .header-left {
      width: 42px;
      height: 64px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #374151;

      &:hover {
        background-color: #e5e7eb;
      }
    }

    // 右侧容器样式
    .header-right {
      margin-left: auto;
      display: flex;

      .header-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        --tw-text-opacity: 1;
        color: rgb(55 65 81 / var(--tw-text-opacity));
        font-size: 0.75rem;
        line-height: 1rem;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;

        &:hover,
        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: none;
        }

        .custom-avatar {
          margin-right: 0.5rem;
        }
      }
    }
  }

  // 提示框项目样式
  .box-item {
    margin-right: 0.25rem;
  }

  // 提示框项样式
  .box-item-tip {
    width: 42px;
    height: 64px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #374151;
    margin-right: 0.5rem;

    &:hover {
      background-color: #e5e7eb;
    }
  }
}
</style>
